import { Alert, Form, Input, Modal, Select, Space, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { AreaBoundaryPicker } from '~/app/components/site-management/AreaBoundaryPicker';
import { SiteAddressAutocomplete } from '~/app/components/site-management/SiteAddressAutocomplete';
import { useTranslation } from 'react-i18next';
import { SiteCoordinatePicker } from '~/app/components/site-management/SiteCoordinatePicker';
import type { SiteFormValues } from '~/app/hooks/site/useSiteManagement';
import type { SiteManagementViewProps } from './SiteManagement.types';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import { EvimoListPanel } from '~/app/components/common/EvimoListPanel/EvimoListPanel';
import { FilterBar } from '~/app/components/common/FilterBar/FilterBar';
import {
  FilterBarActions,
  FilterBarAddButton,
  FilterBarRefreshButton,
} from '~/app/components/common/FilterBar/FilterBarToolbar';

const { Paragraph, Title } = Typography;

export function SiteManagementView({
  cityOptions,
  citySearchValue,
  columns,
  editingSite,
  error,
  form,
  isFetching,
  isLoading,
  isModalOpen,
  isSubmitting,
  onAddressChange,
  onAddressSelect,
  onCityChange,
  onCitySearchChange,
  onCloseModal,
  onCoordinatesChange,
  onLocationSelect,
  onPerimeterChangeAction,
  onOpenCreateModal,
  onPageChange,
  onProvinceChange,
  onProvinceSearchChange,
  onRefresh,
  onSearchChange,
  onSubmitSite,
  page,
  pagination,
  provinceOptions,
  provinceSearchValue,
  searchValue,
  selectedAddress,
  selectedCity,
  selectedLatitude,
  selectedLongitude,
  selectedPerimeter,
  selectedProvince,
  sites,
}: SiteManagementViewProps) {
  const { t } = useTranslation('sites');
  const referenceCoordinates =
    selectedLatitude && selectedLongitude
      ? {
          latitude: Number(selectedLatitude),
          longitude: Number(selectedLongitude),
        }
      : null;
  const addressQuery = [
    selectedAddress?.trim(),
    selectedCity?.name,
    selectedProvince?.name,
    'France',
  ]
    .filter(Boolean)
    .join(', ');
  const isAreaPickerDisabled =
    !addressQuery && !referenceCoordinates && !selectedPerimeter?.length;

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div>
        <Title level={2} style={{ marginBottom: 8 }}>
          {t('title', 'Sites')}
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          {t(
            'description',
            'Create, update and remove sites for the current organization workspace.',
          )}
        </Paragraph>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          message={t('loadFailed', 'Failed to load sites.')}
          description={error.message}
        />
      ) : null}

      <EvimoListPanel>
        <FilterBar>
          <Input
            allowClear
            className="evimo-filter-search"
            prefix={<SearchOutlined />}
            placeholder={t('searchPlaceholder', 'Search sites')}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />

          <FilterBarActions>
            <FilterBarRefreshButton
              ns="sites"
              labelKey="actions.refresh"
              loading={isFetching}
              onClick={onRefresh}
            />
            <FilterBarAddButton
              ns="sites"
              labelKey="actions.addSite"
              defaultLabel="Add site"
              onClick={onOpenCreateModal}
            />
          </FilterBarActions>
        </FilterBar>

        <EvimoTable
          columns={columns}
          dataSource={sites}
          loading={isLoading}
          rowKey="id"
          stickyLastColumn
          pagination={{
            current: page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: false,
            onChange: onPageChange,
          }}
          scroll={{ x: 'max-content' }}
          onRow={(record) => ({
            onClick: () => {
              window.location.href = `/sites/${record.id}`;
            },
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: t('empty', 'No sites found for this organization.'),
          }}
        />
      </EvimoListPanel>

      <Modal
        destroyOnHidden
        open={isModalOpen}
        title={
          editingSite
            ? t('modal.editTitle', 'Edit site')
            : t('modal.createTitle', 'Create site')
        }
        okText={
          editingSite
            ? t('actions.save', 'Save')
            : t('actions.create', 'Create')
        }
        cancelText={t('actions.cancel', 'Cancel')}
        confirmLoading={isSubmitting}
        onCancel={onCloseModal}
        onOk={() => void form.submit()}
      >
        <Form<SiteFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => void onSubmitSite(values)}
        >
          <Form.Item
            name="name"
            label={t('fields.name', 'Site name')}
            rules={[
              {
                required: true,
                message: t('validation.name', 'Site name is required.'),
              },
            ]}
          >
            <Input
              placeholder={t('placeholders.name', 'Enter the site name')}
            />
          </Form.Item>
          <Form.Item name="address" label={t('fields.address', 'Address')}>
            <SiteAddressAutocomplete
              value={selectedAddress}
              placeholder={t('placeholders.address', 'Enter the site address')}
              noResultsText={t(
                'messages.addressAutocompleteNoResults',
                'No address suggestions found.',
              )}
              loadingText={t(
                'messages.addressAutocompleteLoading',
                'Searching addresses...',
              )}
              missingKeyText={t(
                'messages.mapMissingKey',
                'Google Maps is not configured for this environment.',
              )}
              loadFailedText={t(
                'messages.addressAutocompleteFailed',
                'Address suggestions could not be loaded right now.',
              )}
              onChange={onAddressChange}
              onSelect={onAddressSelect}
            />
          </Form.Item>
          <Form.Item
            name="postalCode"
            label={t('fields.postalCode', 'Postal code')}
            rules={[
              {
                validator: async (_, value?: string) => {
                  const normalized = value?.trim() ?? '';
                  if (!normalized || /^\d{5}$/.test(normalized)) {
                    return;
                  }
                  throw new Error(
                    t('validation.postalCode', 'Postal code must be 5 digits.'),
                  );
                },
              },
            ]}
          >
            <Input
              inputMode="numeric"
              maxLength={5}
              placeholder={t(
                'placeholders.postalCode',
                'Enter the 5-digit postal code',
              )}
            />
          </Form.Item>
          <Form.Item
            name="provinceCode"
            label={t('fields.province', 'Province')}
          >
            <Select
              allowClear
              showSearch
              filterOption={false}
              options={provinceOptions}
              searchValue={provinceSearchValue}
              placeholder={t('placeholders.province', 'Search a province')}
              notFoundContent={t(
                'messages.noProvinceResults',
                'No provinces found.',
              )}
              onClear={() => onProvinceSearchChange('')}
              onSearch={onProvinceSearchChange}
              onChange={onProvinceChange}
            />
          </Form.Item>
          <Form.Item name="cityCode" label={t('fields.city', 'City')}>
            <Select
              allowClear
              showSearch
              filterOption={false}
              options={cityOptions}
              searchValue={citySearchValue}
              placeholder={t('placeholders.city', 'Search a city')}
              notFoundContent={t('messages.noCityResults', 'No cities found.')}
              onClear={() => onCitySearchChange('')}
              onSearch={onCitySearchChange}
              onChange={onCityChange}
            />
          </Form.Item>
          <Form.Item label={t('fields.locationPicker', 'Location on map')}>
            <SiteCoordinatePicker
              address={selectedAddress ?? ''}
              cityName={selectedCity?.name}
              provinceName={selectedProvince?.name}
              latitude={selectedLatitude}
              longitude={selectedLongitude}
              selectionRequiredText={t(
                'messages.pickLocationRequiresCityProvince',
                'Select both a province and a city to enable the map picker.',
              )}
              instructionsText={t(
                'messages.pickLocationInstructions',
                'Once the map is centered, click the exact site position to latitude and longitude.',
              )}
              currentCoordinatesText={t(
                'messages.currentCoordinates',
                'Selected coordinates',
              )}
              missingKeyText={t(
                'messages.mapMissingKey',
                'Google Maps is not configured for this environment.',
              )}
              geocodeFailedText={t(
                'messages.mapGeocodeFailed',
                'The selected city and province could not be located on the map.',
              )}
              loadFailedText={t(
                'messages.mapLoadFailed',
                'The map could not be loaded right now.',
              )}
              locatingText={t(
                'messages.mapLocating',
                'Centering the map on the selected city...',
              )}
              onCoordinatesChange={onCoordinatesChange}
              onLocationSelect={onLocationSelect}
            />
          </Form.Item>
          <Form.Item label={t('fields.perimeter', 'Coverage perimeter')}>
            <AreaBoundaryPicker
              addressQuery={addressQuery || null}
              perimeter={selectedPerimeter}
              referenceCoordinates={referenceCoordinates}
              disabled={isAreaPickerDisabled}
              selectionRequiredText={t(
                'messages.pickPerimeterRequiresLocation',
                'Select a city and province or define site coordinates to enable the perimeter editor.',
              )}
              instructionsText={t(
                'messages.pickPerimeterInstructions',
                'Click the map to add perimeter points. Add at least three points, then drag the polygon handles to refine the covered area.',
              )}
              currentPerimeterText={t(
                'messages.currentPerimeterPoints',
                'Perimeter points',
              )}
              missingKeyText={t(
                'messages.mapMissingKey',
                'Google Maps is not configured for this environment.',
              )}
              geocodeFailedText={t(
                'messages.mapGeocodeFailed',
                'The selected city and province could not be located on the map.',
              )}
              loadFailedText={t(
                'messages.mapLoadFailed',
                'The map could not be loaded right now.',
              )}
              locatingText={t(
                'messages.mapLocating',
                'Centering the map on the selected city...',
              )}
              clearText={t('actions.clearPerimeter', 'Clear perimeter')}
              onPerimeterChangeAction={onPerimeterChangeAction}
            />
          </Form.Item>
          <Form.Item name="latitude" label={t('fields.latitude', 'Latitude')}>
            <Input
              inputMode="decimal"
              placeholder={t(
                'placeholders.latitude',
                'Enter the site latitude',
              )}
            />
          </Form.Item>
          <Form.Item
            name="longitude"
            label={t('fields.longitude', 'Longitude')}
          >
            <Input
              inputMode="decimal"
              placeholder={t(
                'placeholders.longitude',
                'Enter the site longitude',
              )}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
